<template>
  <div class="report-wrapper">
    <!-- Modern Hero Section -->
    <div class="report-hero">
      <div class="hero-left">
        <div class="hero-icon">
          <v-icon size="26" color="#3b82f6">mdi-school</v-icon>
        </div>
        <div class="hero-text">
          <h1 class="report-title">Training Detail Report</h1>
          <p class="report-subtitle">Detailed trainee, course, and completion records for training activities</p>
        </div>
      </div>
      <div class="hero-right">
        <v-btn
            class="export-btn"
            color="primary"
            depressed
            @click="exportTrainingDetailPageSelected"
        >
          <v-icon left size="18">mdi-export</v-icon>
          Export Page/Selected
        </v-btn>
        <v-btn
            class="export-btn export-btn--ghost"
            color="primary"
            depressed
            :loading="bulkExportLoading"
            :disabled="bulkExportLoading"
            @click="exportTrainingDetailAll"
        >
          <v-icon left size="18">mdi-database-export</v-icon>
          Export All (Filtered)
        </v-btn>
      </div>
    </div>

    <!-- Summary Statistics Cards -->
    <div class="stats-section">
      <div class="section-header">
        <v-icon size="20" class="section-icon">mdi-chart-box-outline</v-icon>
        <span class="section-title">Summary Statistics</span>
      </div>
      <v-row class="stats-grid">
        <v-col cols="12" sm="6" md="3">
          <div class="stat-card stat-card--blue">
            <div class="stat-icon">
              <v-icon size="24">mdi-account-group</v-icon>
            </div>
            <div class="stat-info">
              <div class="stat-value">{{ trainingSummaryReports.count || 0 }}</div>
              <div class="stat-label">Total Records</div>
            </div>
          </div>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <div class="stat-card stat-card--green">
            <div class="stat-icon">
              <v-icon size="24">mdi-check-circle</v-icon>
            </div>
            <div class="stat-info">
              <div class="stat-value">{{ completedCount }}</div>
              <div class="stat-label">Completed</div>
            </div>
          </div>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <div class="stat-card stat-card--orange">
            <div class="stat-icon">
              <v-icon size="24">mdi-progress-clock</v-icon>
            </div>
            <div class="stat-info">
              <div class="stat-value">{{ inProgressCount }}</div>
              <div class="stat-label">In Progress</div>
            </div>
          </div>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <div class="stat-card stat-card--purple">
            <div class="stat-icon">
              <v-icon size="24">mdi-checkbox-marked-circle</v-icon>
            </div>
            <div class="stat-info">
              <div class="stat-value">{{ selected.length }}</div>
              <div class="stat-label">Selected</div>
            </div>
          </div>
        </v-col>
      </v-row>
    </div>

    <!-- Modern Filter Section -->
    <div class="filter-section">
      <div class="filter-header">
        <div class="filter-header-left">
          <v-icon size="20" class="filter-icon">mdi-filter-variant</v-icon>
          <span class="filter-title">Filters</span>
        </div>
        <v-btn
            v-if="hasActiveFilters"
            text
            small
            color="primary"
            @click="clearAllFilters"
            class="clear-filters-btn"
        >
          <v-icon left small>mdi-close-circle</v-icon>
          Clear All Filters
        </v-btn>
      </div>
      <div class="filter-grid">
        <div class="filter-item">
          <v-text-field
              v-model="filterTrainee"
              prepend-inner-icon="mdi-magnify"
              label="Search by Name"
              clearable
              outlined
              dense
              hide-details
              class="modern-input"
          ></v-text-field>
        </div>
        <div class="filter-item">
          <v-text-field
              v-model="phone"
              prepend-inner-icon="mdi-phone"
              label="Phone Number"
              clearable
              outlined
              dense
              hide-details
              class="modern-input"
          ></v-text-field>
        </div>
        <div class="filter-item">
          <v-autocomplete
              v-model="cpdProviderId"
              :items="CPD_PROVIDERS"
              :loading="cpdLoading"
              item-text="trainingCenterName"
              item-value="id"
              label="CPD Provider"
              prepend-inner-icon="mdi-domain"
              clearable
              outlined
              dense
              hide-details
              class="modern-input"
          ></v-autocomplete>
        </div>
        <div class="filter-item">
          <v-autocomplete
              v-model="regionId"
              :items="REGION"
              :loading="regionLoading"
              item-text="name"
              item-value="id"
              label="Region"
              prepend-inner-icon="mdi-map-marker"
              clearable
              outlined
              dense
              hide-details
              class="modern-input"
          ></v-autocomplete>
        </div>
        <div class="filter-item">
          <v-autocomplete
              v-model="categoryId"
              :items="CATEGORIES"
              :loading="categoryLoading"
              item-text="name"
              item-value="id"
              label="CPD Activity Category"
              prepend-inner-icon="mdi-shape"
              clearable
              outlined
              dense
              hide-details
              class="modern-input"
          ></v-autocomplete>
        </div>
        <div class="filter-item">
          <v-autocomplete
              v-model="cpdActivityId"
              :items="CPD_ACTIVITIES"
              :loading="cpdActLoading"
              item-text="name"
              item-value="id"
              label="CPD Activity"
              prepend-inner-icon="mdi-clipboard-text"
              clearable
              outlined
              dense
              hide-details
              class="modern-input"
          ></v-autocomplete>
        </div>
        <div class="filter-item">
          <v-autocomplete
              v-model="courseId"
              :items="COURSE"
              :loading="courseLoading"
              item-text="courseName"
              item-value="id"
              label="Course"
              prepend-inner-icon="mdi-book-open-variant"
              clearable
              outlined
              dense
              hide-details
              class="modern-input"
          ></v-autocomplete>
        </div>
        <div class="filter-item">
          <v-autocomplete
              v-model="statusValue"
              :items="STATUS"
              item-text="name"
              item-value="id"
              label="Status"
              prepend-inner-icon="mdi-flag"
              clearable
              outlined
              dense
              hide-details
              class="modern-input"
          ></v-autocomplete>
        </div>
        <div class="filter-item">
          <v-select
              v-model="dateRangePreset"
              :items="dateRangePresets"
              item-text="text"
              item-value="value"
              clearable
              outlined
              dense
              hide-details
              label="Quick Range"
              class="modern-input"
          ></v-select>
        </div>
        <div class="filter-item">
          <v-select
              v-model="dateRangeYear"
              :items="dateRangeYears"
              clearable
              outlined
              dense
              hide-details
              label="Year"
              class="modern-input"
          ></v-select>
        </div>
        <div v-if="dateRangePreset && dateRangePreset !== 'yearly'" class="filter-item">
          <v-select
              v-model="dateRangePeriod"
              :items="getPresetPeriodOptions(dateRangePreset)"
              :disabled="!dateRangePreset || dateRangePreset === 'yearly'"
              clearable
              outlined
              dense
              hide-details
              label="Period"
              class="modern-input"
          ></v-select>
        </div>
        <div class="filter-item">
          <v-menu
              ref="startMenu"
              v-model="startMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="290px"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                  v-model="startDate"
                  label="Start Date"
                  prepend-inner-icon="mdi-calendar"
                  readonly
                  clearable
                  outlined
                  dense
                  hide-details
                  class="modern-input"
                  v-bind="attrs"
                  v-on="on"
              ></v-text-field>
            </template>
            <v-date-picker
                v-model="startDate"
                no-title
                scrollable
                @input="startMenu = false"
            ></v-date-picker>
          </v-menu>
        </div>
        <div class="filter-item">
          <v-menu
              ref="endMenu"
              v-model="endMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="290px"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                  v-model="endDate"
                  label="End Date"
                  prepend-inner-icon="mdi-calendar"
                  readonly
                  clearable
                  outlined
                  dense
                  hide-details
                  class="modern-input"
                  v-bind="attrs"
                  v-on="on"
              ></v-text-field>
            </template>
            <v-date-picker
                v-model="endDate"
                no-title
                scrollable
                @input="endMenu = false"
            ></v-date-picker>
          </v-menu>
        </div>
        <div class="filter-item">
          <v-select
              v-model="isEmployeeValue"
              :items="employmentOptions"
              item-text="text"
              item-value="value"
              prepend-inner-icon="mdi-briefcase-account"
              label="Employment Status"
              clearable
              outlined
              dense
              hide-details
              class="modern-input"
          ></v-select>
        </div>
      </div>
    </div>

    <div class="ceu-summary-section">
      <div class="section-header ceu-summary-header">
        <v-icon size="20" class="section-icon">mdi-certificate</v-icon>
        <span class="section-title">Annual CEU Summary</span>
        <v-spacer></v-spacer>
        <v-btn
            class="export-btn export-btn--ghost"
            color="primary"
            depressed
            @click="exportAnnualCeuSummary"
        >
          <v-icon left size="18">mdi-export</v-icon>
          Export Summary
        </v-btn>
      </div>
      <v-data-table
          :headers="ceuSummaryHeaders"
          :items="ceuSummary"
          :loading="ceuSummaryLoading"
          item-key="summaryKey"
          class="modern-table elevation-0"
          loading-text="Loading CEU summaries..."
          :footer-props="{
            'items-per-page-options': [10, 25, 50, 100],
            'show-first-last-page': true
          }"
      >
        <template v-slot:progress>
          <v-progress-linear
              color="primary"
              indeterminate
              height="3"
          ></v-progress-linear>
        </template>
        <template v-slot:[`item.totalCeu`]="{ item }">
          <v-chip color="indigo lighten-4" text-color="indigo darken-2" small>
            {{ item.totalCeu || 0 }} CEU
          </v-chip>
        </template>
        <template v-slot:no-data>
          <div class="no-data-state">
            <v-icon size="48" color="grey lighten-1">mdi-certificate-outline</v-icon>
            <h3 class="mt-3 text-h6 grey--text text--darken-1">No CEU summaries found</h3>
            <p class="grey--text">Try adjusting your filters or date range</p>
          </div>
        </template>
      </v-data-table>
    </div>

    <!-- Data Table Section -->
    <div class="table-section">
      <v-data-table
          v-model="selected"
          :headers="headers"
          :items="trainingSummaryReports.rows"
          :loading="loading"
          :options.sync="options"
          :server-items-length="trainingSummaryReports.count"
          item-key="id"
          class="modern-table elevation-0"
          loading-text="Loading training records..."
          show-select
          :footer-props="{
            'items-per-page-options': [10, 25, 50, 100],
            'show-first-last-page': true
          }"
      >
        <!-- Loading slot -->
        <template v-slot:progress>
          <v-progress-linear
              color="primary"
              indeterminate
              height="3"
          ></v-progress-linear>
        </template>

        <!-- Full Name column -->
        <template v-slot:[`item.trainee.fullName`]="{ item }">
          <div class="trainee-info">
            <v-avatar size="36" color="primary" class="mr-3">
              <span class="white--text text-body-2">
                {{ getInitials(item.trainee) }}
              </span>
            </v-avatar>
            <div>
              <div class="trainee-name">{{ getFullName(item.trainee) }}</div>
              <div class="trainee-phone">{{ item.trainee?.phoneNumber || '-' }}</div>
            </div>
          </div>
        </template>

        <!-- Gender column -->
        <template v-slot:[`item.trainee.gender`]="{ item }">
          <v-chip
              :color="item.trainee?.gender === 'Male' ? 'blue lighten-4' : 'pink lighten-4'"
              :text-color="item.trainee?.gender === 'Male' ? 'blue darken-2' : 'pink darken-2'"
              small
              label
          >
            <v-icon left x-small>
              {{ item.trainee?.gender === 'Male' ? 'mdi-gender-male' : 'mdi-gender-female' }}
            </v-icon>
            {{ item.trainee?.gender || '-' }}
          </v-chip>
        </template>

        <!-- Employee status column -->
        <template v-slot:[`item.trainee.unemployed`]="{ item }">
          <v-chip
              :color="!item.trainee?.unemployed ? 'green lighten-4' : 'grey lighten-2'"
              :text-color="!item.trainee?.unemployed ? 'green darken-2' : 'grey darken-1'"
              small
              label
          >
            <v-icon left x-small>
              {{ !item.trainee?.unemployed ? 'mdi-briefcase-check' : 'mdi-briefcase-off' }}
            </v-icon>
            {{ item.trainee?.unemployed ? 'No' : 'Yes' }}
          </v-chip>
        </template>

        <!-- CEU column -->
        <template v-slot:[`item.categoryDisplay`]="{ item }">
          <span class="category-text">
            {{ getCategoryName(item) }}
          </span>
        </template>

        <!-- Course column -->
        <template v-slot:[`item.courseDisplay`]="{ item }">
          <span class="course-text">
            {{ getCourseName(item) }}
          </span>
        </template>

        <!-- CEU column -->
        <template v-slot:[`item.ceuDisplay`]="{ item }">
          <v-chip color="purple lighten-4" text-color="purple darken-2" small>
            {{ getCeuValue(item) || 0 }} CEU
          </v-chip>
        </template>

        <!-- CPD Provider column -->
        <template v-slot:[`item.providerDisplay`]="{ item }">
          <div class="provider-name">
            {{ getProviderName(item) }}
          </div>
        </template>

        <!-- Status column -->
        <template v-slot:[`item.statusDisplay`]="{ item }">
          <v-chip
              :color="getStatusChipColor(item)"
              :text-color="getStatusChipTextColor(item)"
              small
              label
          >
            {{ getStatusName(item) }}
          </v-chip>
        </template>

        <!-- Training dates columns -->
        <template v-slot:[`item.startDateDisplay`]="{ item }">
          <span class="date-text">
            {{ formatDate(getProgramDate(item, 'startDate')) }}
          </span>
        </template>

        <template v-slot:[`item.endDateDisplay`]="{ item }">
          <span class="date-text">
            {{ formatDate(getProgramDate(item, 'endDate')) }}
          </span>
        </template>

        <!-- No data slot -->
        <template v-slot:no-data>
          <div class="no-data-state">
            <v-icon size="64" color="grey lighten-1">mdi-file-search-outline</v-icon>
            <h3 class="mt-4 text-h6 grey--text text--darken-1">No training records found</h3>
            <p class="grey--text">Try adjusting your filters or search criteria</p>
          </div>
        </template>
      </v-data-table>
    </div>
  </div>
</template>

<script>
import { TrainingSummaryService } from "./training-summary-service";
import { getCategories, getRegionList } from "@/utils/helper";
import { TraineeService } from "../../Trainees/trainees.service";
import { LookupService } from "../../../common/api.service";
import { getProgramsDrp } from "../../../utils/helper";
import reportDateRangeMixin from "@/mixins/reportDateRangeMixin";

export default {
  mixins: [reportDateRangeMixin],
  data() {
    return {
      options: {
        page: 1,
        itemsPerPage: 10,
      },

      CATEGORIES: [],
      CPD_ACTIVITIES: [],
      STATUS: [],
      COURSE: [],
      CPD_PROVIDERS: [],
      REGION: [],
      cpdActLoading: false,
      categoryLoading: false,
      statusLoading: false,
      cpdLoading: false,
      courseLoading: false,
      regionLoading: false,

      filterTrainee: null,
      startDate: null,
      endDate: null,
      dateRangePreset: null,
      dateRangeYear: null,
      dateRangePeriod: null,
      cpdActivityId: null,
      categoryId: null,
      statusValue: null,
      cpdProviderId: null,
      courseId: null,
      regionId: null,
      phone: null,
      isEmployeeValue: null,
      employmentOptions: [
        { text: "Employee", value: true },
        { text: "Non-Employee", value: false },
      ],

      startMenu: false,
      endMenu: false,

      traineStatusIds: { passId: "", failId: "" },
      traineeStatus: [],

      trainingSummaryReports: { rows: [], count: 0 },
      ceuSummary: [],
      ceuSummaryLoading: false,
      headers: [
        { text: "Trainee", value: "trainee.fullName", width: "220px" },
        { text: "Gender", value: "trainee.gender", width: "100px" },
        { text: "Employee", value: "trainee.unemployed", width: "110px" },
        { text: "Profession", value: "trainee.healthProfession.name", width: "150px" },
        { text: "Facility", value: "trainee.facility.name", width: "180px" },
        { text: "Category", value: "categoryDisplay", width: "150px" },
        { text: "Course", value: "courseDisplay", width: "180px" },
        { text: "CEU", value: "ceuDisplay", width: "90px" },
        { text: "CPD Provider", value: "providerDisplay", width: "180px" },
        { text: "Status", value: "statusDisplay", width: "130px" },
        { text: "Start Date", value: "startDateDisplay", width: "120px" },
        { text: "End Date", value: "endDateDisplay", width: "120px" },
      ],
      ceuSummaryHeaders: [
        { text: "Participant", value: "fullName", width: "220px" },
        { text: "Phone Number", value: "phoneNumber", width: "140px" },
        { text: "Year", value: "year", width: "90px" },
        { text: "Total CEU", value: "totalCeu", width: "120px" },
        { text: "Trainings", value: "trainingCount", width: "110px" },
      ],
      ceuSummaryExportFields: {
        "Full Name": "fullName",
        "Phone Number": "phoneNumber",
        Year: "year",
        "Total CEU": "totalCeu",
        "Trainings Count": "trainingCount",
      },

      loading: false,
      bulkExportLoading: false,
      selected: [],
      debounceTimer: null,
    };
  },
  computed: {
    trainingExportFields() {
      return {
        "Full Name": {
          callback: (_, row) => this.getFullName(row?.trainee),
        },
        "Phone Number": {
          callback: (_, row) => row?.trainee?.phoneNumber || null,
        },
        Gender: {
          callback: (_, row) => row?.trainee?.gender || null,
        },
        Employee: {
          callback: (_, row) => this.getEmploymentLabel(row?.trainee),
        },
        Profession: {
          callback: (_, row) => row?.trainee?.healthProfession?.name || null,
        },
        "Facility Name": {
          callback: (_, row) => row?.trainee?.facility?.name || null,
        },
        "CPD Activity Category": {
          callback: (_, row) => this.getCategoryName(row),
        },
        Course: {
          callback: (_, row) => this.getCourseName(row),
        },
        CEU: {
          callback: (_, row) => this.getCeuValue(row),
        },
        "CPD Provider": {
          callback: (_, row) => this.getProviderName(row),
        },
        Status: {
          callback: (_, row) => this.getStatusName(row),
        },
        "Training Start Date": {
          callback: (_, row) => this.getProgramDate(row, "startDate"),
        },
        "Training End Date": {
          callback: (_, row) => this.getProgramDate(row, "endDate"),
        },
      };
    },
    hasActiveFilters() {
      return (
          this.filterTrainee ||
          this.phone ||
          this.cpdProviderId ||
          this.regionId ||
          this.categoryId ||
          this.cpdActivityId ||
          this.courseId ||
          this.statusValue ||
          this.startDate ||
          this.endDate ||
          this.isEmployeeValue !== null
      );
    },
    completedCount() {
      if (!this.trainingSummaryReports.rows) return 0;
      return this.trainingSummaryReports.rows.filter(
          (r) => this.getStatusId(r) && this.getStatusId(r) === this.traineStatusIds.passId
      ).length;
    },
    inProgressCount() {
      if (!this.trainingSummaryReports.rows) return 0;
      return this.trainingSummaryReports.rows.filter(
          (r) => !this.getStatusId(r)
      ).length;
    },
  },
  async beforeMount() {
    this.initLookups();
  },
  watch: {
    options: {
      handler() {
        this.fetchTSummaryReports();
      },
      deep: true,
    },
    filterTrainee() {
      this.debouncedFetch();
    },
    phone() {
      this.debouncedFetch();
    },
    startDate() {
      this.refreshFilteredData({ resetPage: true });
    },
    endDate() {
      this.refreshFilteredData({ resetPage: true });
    },
    dateRangePreset(val) {
      this.applyPresetWithSelection(val, "startDate", "endDate", "dateRangeYear", "dateRangePeriod");
    },
    dateRangeYear() {
      if (this.dateRangePreset) {
        this.applyPresetWithSelection(this.dateRangePreset, "startDate", "endDate", "dateRangeYear", "dateRangePeriod");
      }
    },
    dateRangePeriod() {
      if (this.dateRangePreset) {
        this.applyPresetWithSelection(this.dateRangePreset, "startDate", "endDate", "dateRangeYear", "dateRangePeriod");
      }
    },
    async categoryId(val) {
      await this.fetchCpdActivity(val);
      if (
          this.cpdActivityId &&
          !this.CPD_ACTIVITIES.some((item) => Number(item?.id) === Number(this.cpdActivityId))
      ) {
        this.cpdActivityId = null;
        return;
      }
      this.refreshFilteredData({ resetPage: true });
    },
    cpdActivityId() {
      this.refreshFilteredData({ resetPage: true });
    },
    statusValue() {
      this.refreshFilteredData({ resetPage: true });
    },
    cpdProviderId() {
      this.refreshFilteredData({ resetPage: true });
    },
    courseId() {
      this.refreshFilteredData({ resetPage: true });
    },
    regionId() {
      this.refreshFilteredData({ resetPage: true });
    },
    isEmployeeValue() {
      this.refreshFilteredData({ resetPage: true });
    },
  },
  methods: {
    buildFilterParams({ includePagination = true } = {}) {
      const params = new URLSearchParams();
      if (includePagination) {
        const { page, itemsPerPage } = this.options;
        params.append("page", page);
        params.append("pageSize", itemsPerPage);
      }

      if (this.filterTrainee) params.append("name", this.filterTrainee);
      if (this.startDate) params.append("startDate", this.startDate);
      if (this.endDate) params.append("endDate", this.endDate);
      if (this.categoryId) params.append("categoryId", this.categoryId);
      if (this.cpdActivityId) params.append("cpdActivityId", this.cpdActivityId);
      if (this.statusValue !== null) params.append("statusId", this.statusValue);
      if (this.phone) params.append("phone", this.phone);
      if (this.cpdProviderId) params.append("cpdId", this.cpdProviderId);
      if (this.courseId) params.append("courseId", this.courseId);
      if (this.regionId) params.append("regionId", this.regionId);
      if (this.isEmployeeValue !== null) params.append("isEmployed", this.isEmployeeValue);

      return params;
    },
    getQueryString(params) {
      const query = params.toString();
      return query ? `?${query}` : "";
    },
    refreshFilteredData({ resetPage = false } = {}) {
      if (resetPage && this.options.page !== 1) {
        this.options = { ...this.options, page: 1 };
      } else {
        this.fetchTSummaryReports();
      }
      this.fetchCeuSummary();
    },
    debouncedFetch() {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => {
        this.refreshFilteredData({ resetPage: true });
      }, 400);
    },
    clearAllFilters() {
      this.filterTrainee = null;
      this.phone = null;
      this.cpdProviderId = null;
      this.regionId = null;
      this.categoryId = null;
      this.cpdActivityId = null;
      this.courseId = null;
      this.statusValue = null;
      this.startDate = null;
      this.endDate = null;
      this.isEmployeeValue = null;
      this.dateRangePreset = null;
      this.dateRangeYear = null;
      this.dateRangePeriod = null;
    },
    getInitials(trainee) {
      if (!trainee) return "?";
      const first = trainee.firstName?.charAt(0) || "";
      const last = trainee.lastName?.charAt(0) || "";
      return (first + last).toUpperCase() || "?";
    },
    getFullName(trainee) {
      if (!trainee) return "-";
      const parts = [
        trainee.prefix,
        trainee.firstName,
        trainee.middleName,
        trainee.lastName,
      ].filter(Boolean);
      return parts.join(" ") || "-";
    },
    getEmploymentLabel(trainee) {
      if (!trainee) return "-";
      return trainee.unemployed ? "No" : "Yes";
    },
    getCategoryName(item) {
      return (
          item?.new_trainees_course?.program?.category?.name ||
          item?.newt_cat2_course?.program?.category?.name ||
          "-"
      );
    },
    getCourseName(item) {
      return (
          item?.new_trainees_course?.course?.courseName ||
          item?.newt_cat2_course?.program?.courseTitleCat2 ||
          "-"
      );
    },
    getCeuValue(item) {
      return (
          item?.new_trainees_course?.course?.continualEducationUnit ||
          item?.newt_cat2_course?.program?.ceu ||
          0
      );
    },
    getProviderName(item) {
      return (
          item?.new_trainees_course?.program?.cpdProvider?.trainingCenterName ||
          item?.newt_cat2_course?.program?.cpdProvider?.trainingCenterName ||
          "-"
      );
    },
    getProgramDate(item, field) {
      return (
          item?.new_trainees_course?.program?.[field] ||
          item?.newt_cat2_course?.program?.[field] ||
          null
      );
    },
    getStatusId(item) {
      return (
          item?.new_trainees_course?.traineeStatusId ||
          item?.newt_cat2_course?.traineeStatusId ||
          null
      );
    },
    getStatusName(item) {
      return (
          item?.new_trainees_course?.traineeStatus?.name ||
          item?.newt_cat2_course?.traineeStatus?.name ||
          "Un Completed"
      );
    },
    getStatusChipColor(item) {
      const status = this.getStatusName(item).toLowerCase();
      if (status.includes("pass") || status.includes("complete")) return "green lighten-4";
      if (status.includes("fail")) return "red lighten-4";
      return "orange lighten-4";
    },
    getStatusChipTextColor(item) {
      const status = this.getStatusName(item).toLowerCase();
      if (status.includes("pass") || status.includes("complete")) return "green darken-2";
      if (status.includes("fail")) return "red darken-2";
      return "orange darken-2";
    },
    formatDate(date) {
      if (!date) return "-";
      return new Date(date).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    },
    async initLookups() {
      this.categoryLoading = true;
      this.CATEGORIES = await getCategories();
      this.categoryLoading = false;

      this.fetchTSummaryReports();
      this.fetchCeuSummary();
      this.fetchCpdActivity();
      this.fetchTraineeStatus();
      this.fetchCpdProviders();
      this.fetchCourses();
      this.fetchRegions();
    },
    async fetchCpdActivity(id = null) {
      this.cpdActLoading = true;
      const programs = await getProgramsDrp(id);
      this.CPD_ACTIVITIES = programs.data;
      this.cpdActLoading = false;
    },
    async fetchCpdProviders() {
      this.cpdLoading = true;
      const res = await TrainingSummaryService.getTrainingCentersDrp();
      this.CPD_PROVIDERS = res?.data;
      this.cpdLoading = false;
    },
    async fetchRegions() {
      this.regionLoading = true;
      this.REGION = await getRegionList();
      this.regionLoading = false;
    },
    async fetchCourses() {
      this.courseLoading = true;
      const course = await LookupService.getCourses();
      this.COURSE = course?.data?.data;
      this.courseLoading = false;
    },
    async fetchTraineeStatus() {
      this.statusLoading = true;
      this.traineeStatus = await TraineeService.getTraineeStatus();
      this.STATUS = [...this.traineeStatus.data, { name: "Un Completed", id: 0 }];
      this.statusLoading = false;
      this.traineeStatus.data.forEach((e) => {
        if (e.code === "PASS") this.traineStatusIds.passId = e.id;
        if (e.code === "FAIL") this.traineStatusIds.failId = e.id;
      });
    },
    async fetchTSummaryReports() {
      this.loading = true;
      const params = this.buildFilterParams({ includePagination: true });

      try {
        const res = await TraineeService.getNewTraineeAssignList(
            this.getQueryString(params)
        );
        if (res.status === 200) {
          this.trainingSummaryReports = res.data;
        } else {
          this.$toast.error("Failed to fetch training records. Please try again.");
        }
      } catch (error) {
        this.$toast.error("An error occurred while fetching training records.");
      } finally {
        this.loading = false;
      }
    },
    async fetchCeuSummary() {
      this.ceuSummaryLoading = true;
      const params = this.buildFilterParams({ includePagination: false });
      try {
        const res = await TrainingSummaryService.getAnnualCeuSummary(
            this.getQueryString(params)
        );
        if (res.status === 200) {
          this.ceuSummary = res.data || [];
        } else {
          this.$toast.error("Failed to fetch CEU summary. Please try again.");
        }
      } catch (error) {
        this.$toast.error("An error occurred while fetching CEU summary.");
      } finally {
        this.ceuSummaryLoading = false;
      }
    },
    getValueByPath(source, path) {
      if (!source || !path) return null;
      return path.split(".").reduce((acc, key) => {
        if (acc === null || acc === undefined) return null;
        return acc[key];
      }, source);
    },
    resolveExportValue(row, fieldConfig) {
      if (typeof fieldConfig === "string") {
        return this.getValueByPath(row, fieldConfig);
      }

      if (fieldConfig && typeof fieldConfig === "object") {
        const rawValue = this.getValueByPath(row, fieldConfig.field);
        if (typeof fieldConfig.callback === "function") {
          return fieldConfig.callback(rawValue, row);
        }
        return rawValue;
      }

      return null;
    },
    escapeCsv(value) {
      const stringValue = value === null || value === undefined ? "" : String(value);
      return `"${stringValue.replace(/"/g, '""')}"`;
    },
    buildCsvContent(rows, fields) {
      const headers = Object.keys(fields || {});
      const csvRows = [headers.map(this.escapeCsv).join(",")];

      rows.forEach((row) => {
        const csvRow = headers.map((header) => {
          const value = this.resolveExportValue(row, fields[header]);
          return this.escapeCsv(value);
        });
        csvRows.push(csvRow.join(","));
      });

      return csvRows.join("\r\n");
    },
    downloadCsv(content, filename) {
      const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);

      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },
    exportRowsToCsv(rows, fields, filename, emptyMessage) {
      if (!rows || !rows.length) {
        this.$toast.info(emptyMessage);
        return;
      }

      const csvContent = this.buildCsvContent(rows, fields);
      this.downloadCsv(csvContent, filename);
    },
    exportTrainingDetailPageSelected() {
      const rows = this.selected.length > 0 ? this.selected : this.trainingSummaryReports.rows;
      this.exportRowsToCsv(
          rows,
          this.trainingExportFields,
          "trainingDetailReport.csv",
          "No training detail data available to export."
      );
    },
    async exportTrainingDetailAll() {
      this.bulkExportLoading = true;
      try {
        const rows = await this.fetchAllFiltered();
        this.exportRowsToCsv(
            rows,
            this.trainingExportFields,
            "trainingDetailReport_All.csv",
            "No filtered training detail data available to export."
        );
      } finally {
        this.bulkExportLoading = false;
      }
    },
    exportAnnualCeuSummary() {
      this.exportRowsToCsv(
          this.ceuSummary,
          this.ceuSummaryExportFields,
          "annualCeuSummary.csv",
          "No annual CEU summary data available to export."
      );
    },
    async fetchAllFiltered() {
      const params = this.buildFilterParams({ includePagination: false });
      try {
        const res = await TraineeService.getNewTraineeAssignList(
            this.getQueryString(params)
        );
        if (res.status === 200) {
          return res.data?.rows || [];
        }
        this.$toast.error("Failed to fetch export data. Please try again.");
        return [];
      } catch (error) {
        this.$toast.error("An error occurred while preparing export data.");
        return [];
      }
    },
  },
  beforeDestroy() {
    clearTimeout(this.debounceTimer);
  },
};
</script>

<style scoped>
/* ===== Report Wrapper ===== */
.report-wrapper {
  min-height: 100vh;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  padding: 20px;
}

/* ===== Hero Section ===== */
.report-hero {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  background: #ffffff;
  border-radius: 14px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04);
  border: 1px solid rgba(0, 0, 0, 0.04);
  margin-bottom: 24px;
}

.hero-right {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.hero-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.hero-icon {
  width: 52px;
  height: 52px;
  background: linear-gradient(135deg, #dbeafe, #bfdbfe);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.hero-text {
  display: flex;
  flex-direction: column;
}

.report-title {
  margin: 0;
  font-size: 1.35rem;
  font-weight: 700;
  color: #1e293b;
}

.report-subtitle {
  margin: 4px 0 0;
  font-size: 0.85rem;
  color: #64748b;
}

.export-btn {
  border-radius: 10px !important;
  text-transform: none !important;
  font-weight: 600 !important;
  letter-spacing: 0 !important;
  padding: 0 20px !important;
}

.export-btn--ghost {
  background: #ffffff !important;
  border: 1px solid #dbeafe !important;
  color: #2563eb !important;
}

/* ===== Stats Section ===== */
.stats-section {
  margin-bottom: 24px;
}

.section-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
}

.section-icon {
  color: #3b82f6;
}

.section-title {
  font-size: 1rem;
  font-weight: 600;
  color: #1e293b;
}

.stats-grid {
  margin: 0 -8px;
}

.stat-card {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px;
  background: #ffffff;
  border-radius: 14px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04);
  border: 1px solid rgba(0, 0, 0, 0.04);
  transition: all 0.25s ease;
}

.stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.08);
}

.stat-icon {
  width: 52px;
  height: 52px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.stat-card--blue .stat-icon {
  background: linear-gradient(135deg, #dbeafe, #bfdbfe);
  color: #2563eb;
}

.stat-card--green .stat-icon {
  background: linear-gradient(135deg, #dcfce7, #bbf7d0);
  color: #16a34a;
}

.stat-card--orange .stat-icon {
  background: linear-gradient(135deg, #ffedd5, #fed7aa);
  color: #ea580c;
}

.stat-card--purple .stat-icon {
  background: linear-gradient(135deg, #ede9fe, #ddd6fe);
  color: #7c3aed;
}

.stat-info {
  display: flex;
  flex-direction: column;
}

.stat-value {
  font-size: 1.75rem;
  font-weight: 700;
  color: #1e293b;
  line-height: 1.2;
}

.stat-label {
  font-size: 0.85rem;
  color: #64748b;
  margin-top: 2px;
}

/* ===== Filter Section ===== */
.filter-section {
  background: #ffffff;
  border-radius: 14px;
  padding: 20px 24px;
  margin-bottom: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04);
  border: 1px solid rgba(0, 0, 0, 0.04);
}

.filter-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #f1f5f9;
}

.filter-header-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.filter-icon {
  color: #64748b;
}

.filter-title {
  font-size: 0.9rem;
  font-weight: 600;
  color: #475569;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.clear-filters-btn {
  text-transform: none !important;
  font-weight: 500 !important;
}

.filter-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
}

.filter-item {
  min-width: 0;
}

.filter-item--switch {
  display: flex;
  align-items: center;
}

.modern-input :deep(.v-input__slot) {
  border-radius: 10px !important;
  background: #f8fafc !important;
}

.modern-input :deep(.v-input__slot:hover) {
  background: #f1f5f9 !important;
}

/* ===== CEU Summary ===== */
.ceu-summary-section {
  background: #ffffff;
  border-radius: 14px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04);
  border: 1px solid rgba(0, 0, 0, 0.04);
  margin-bottom: 24px;
  overflow: hidden;
}

.ceu-summary-header {
  margin-bottom: 0;
  padding: 16px 20px;
  border-bottom: 1px solid #f1f5f9;
}

/* ===== Table Section ===== */
.table-section {
  background: #ffffff;
  border-radius: 14px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04);
  border: 1px solid rgba(0, 0, 0, 0.04);
  overflow: hidden;
}

.modern-table {
  border-radius: 14px;
}

.modern-table :deep(.v-data-table-header) {
  background: #f8fafc;
}

.modern-table :deep(.v-data-table-header th) {
  font-weight: 600 !important;
  color: #475569 !important;
  font-size: 0.8rem !important;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 16px 12px !important;
  border-bottom: 2px solid #e2e8f0 !important;
}

.modern-table :deep(tbody tr) {
  transition: background-color 0.15s ease;
}

.modern-table :deep(tbody tr:hover) {
  background: #f8fafc !important;
}

.modern-table :deep(tbody td) {
  padding: 12px !important;
  border-bottom: 1px solid #f1f5f9 !important;
}

.modern-table :deep(.v-data-footer) {
  border-top: 1px solid #e2e8f0;
  padding: 12px 16px;
}

/* Trainee info cell */
.trainee-info {
  display: flex;
  align-items: center;
}

.trainee-name {
  font-weight: 600;
  color: #1e293b;
  font-size: 0.9rem;
}

.trainee-phone {
  font-size: 0.75rem;
  color: #64748b;
  margin-top: 2px;
}

.provider-name {
  font-size: 0.85rem;
  color: #475569;
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.category-text,
.course-text {
  font-size: 0.85rem;
  color: #475569;
}

.date-text {
  font-size: 0.85rem;
  color: #64748b;
}

/* No data state */
.no-data-state {
  padding: 60px 20px;
  text-align: center;
}

/* ===== Responsive ===== */
@media (max-width: 768px) {
  .report-wrapper {
    padding: 12px;
  }

  .report-hero {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }

  .hero-right {
    width: 100%;
  }

  .export-btn {
    width: 100%;
  }

  .filter-grid {
    grid-template-columns: 1fr;
  }
}
</style>
